import { ref } from 'vue';
import { Address, createPublicClient, http, parseUnits, encodePacked } from 'viem';
import { quoterV2Abi, quoterV2MultiAbi } from '../lib/abi';
import { useTokens } from './tokens';
import { useChainStore } from '../stores/chain';

function getClient() {
  const chain = useChainStore();
  const cfg = chain.current;
  if (!cfg.rpcUrl) throw new Error('RPC URL not configured for selected chain');
  return createPublicClient({ chain: cfg.chain, transport: http(cfg.rpcUrl) });
}

export function useQuote() {
  const { selectedFrom, selectedTo } = useTokens();
  const loading = ref(false);
  const error = ref<string | null>(null);
  const amountOut = ref<string | null>(null);
  const gasEstimate = ref<string | null>(null);
  const usedFee = ref<number | null>(null);
  const routePath = ref<`0x${string}` | null>(null);

  async function getQuote(amountInStr: string) {
    error.value = null;
    amountOut.value = null;
    gasEstimate.value = null;
    usedFee.value = null;
    routePath.value = null;
    const chain = useChainStore();
    const quoter = chain.current.uniswap?.quoter as Address | undefined;
    if (!quoter) {
      error.value = 'Quoter address not configured';
      return;
    }
    if (!selectedFrom.value || !selectedTo.value) {
      error.value = 'Select tokens';
      return;
    }
    loading.value = true;
    try {
      const publicClient = getClient();
      const amountIn = parseUnits(amountInStr || '0', selectedFrom.value.decimals);
      // Try common V3 fees, preferring configured fee first
      const pref = Number(chain.current.uniswap?.poolFee || 3000);
      const fees = Array.from(new Set([pref, 500, 3000, 10000])).filter(Boolean) as number[];
      let lastErr: any = null;
      for (const fee of fees) {
        try {
          const [out, , , gas] = (await publicClient.readContract({
            address: quoter,
            abi: quoterV2Abi,
            functionName: 'quoteExactInputSingle',
            args: [
              {
                tokenIn: selectedFrom.value.address,
                tokenOut: selectedTo.value.address,
                fee,
                recipient: '0x0000000000000000000000000000000000000000',
                amountIn,
                sqrtPriceLimitX96: 0n
              }
            ]
          })) as any;
          amountOut.value = out.toString();
          gasEstimate.value = gas.toString();
          usedFee.value = fee;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      // Try 2-hop via common base assets if direct pool failed
      if (!usedFee.value) {
        const bases = await discoverBaseAssets();
        outer: for (const mid of bases) {
          for (const f1 of fees) {
            for (const f2 of fees) {
              try {
                const path = encodePacked(
                  ['address','uint24','address','uint24','address'],
                  [selectedFrom.value.address, f1, mid.address, f2, selectedTo.value.address]
                );
                const [out, , , gas] = (await publicClient.readContract({
                  address: quoter,
                  abi: quoterV2MultiAbi,
                  functionName: 'quoteExactInput',
                  args: [path, amountIn]
                })) as any;
                amountOut.value = out.toString();
                gasEstimate.value = gas.toString();
                usedFee.value = f2; // display last hop fee as reference
                routePath.value = path as any;
                lastErr = null;
                break outer;
              } catch (e) {
                lastErr = e;
              }
            }
          }
        }
        if (!usedFee.value) {
          const feeText = fees.map(f => `${f/10000}%`).join(', ');
          const basesText = bases.map(b => b.symbol).join('/');
          error.value = `No route found for selected pair (tried direct fees ${feeText} and via ${basesText}).`;
          if (lastErr) console.warn('[quote] last error', lastErr);
        }
      }
    } catch (e: any) {
      error.value = e?.shortMessage || e?.message || 'Quote failed';
    } finally {
      loading.value = false;
    }
  }

  return { loading, error, amountOut, gasEstimate, usedFee, routePath, getQuote };
}

// Discover base assets (WETH/USDC/USDT) addresses from tokenlist for current chain
async function discoverBaseAssets(): Promise<Array<{ address: Address, symbol: string }>> {
  try {
    const { useChainStore } = await import('../stores/chain');
    const chain = useChainStore();
    const res = await fetch('/tokenlist.json');
    const json = await res.json();
    const items = (json.tokens || []).filter((t: any) => t.chainId === chain.current.id);
    const want = new Set(['WETH','USDC','USDT','DAI']);
    const bases = [] as Array<{ address: Address, symbol: string }>;
    const seen = new Set<string>();
    for (const t of items) {
      const sym = String(t.symbol || '').toUpperCase();
      if (!want.has(sym)) continue;
      const key = String(t.address).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      bases.push({ address: t.address as Address, symbol: t.symbol });
    }
    return bases;
  } catch {
    return [];
  }
}
